import React, { useState, useEffect } from 'react'
import GoogleLoginButton from 'components/shared/GoogleLoginButton'
import { useModalBehavior } from 'hooks'
import { joinCommunity } from 'utils/teachingTeamApi'
import { trackEvent } from 'utils/analytics'

export default function JoinCommunityModal({ isOpen, onClose, onSuccess, teamId, translations }) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const { handleOverlayClick } = useModalBehavior(isOpen, onClose)

  const t = translations || {}

  if (!isOpen) return null

  const redirectUrl = window.location.origin + window.location.pathname + '?join_community=1'

  const validateEmail = (value) => {
    const trimmed = value.trim()
    if (!trimmed) return t.email_required || 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return t.email_invalid || 'Invalid email format'
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validationError = validateEmail(email)
    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await joinCommunity(teamId, { email: email.trim() })
      onSuccess()
    } catch (err) {
      setError(err.message || t.error || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4" onClick={handleOverlayClick}>
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — mobile: back arrow, desktop: blue bar */}
        <div className="shrink-0">
          <div className="lg:hidden px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-3">
            <button type="button" onClick={onClose} className="flex items-center text-gray-600 cursor-pointer">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-gray-900">
              {t.title || 'Subscribe'}
            </span>
          </div>
          <div className="hidden lg:flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: '#E6F0FF' }}>
            <span className="font-semibold" style={{ color: '#3366FF' }}>
              {t.title || 'Subscribe'}
            </span>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <p className="text-sm text-gray-500 mb-5">
            {t.subtitle || 'Enter your email to receive the latest course and event updates'}
          </p>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (error) setError(null)
                }}
                placeholder={t.email_placeholder || 'your@email.com'}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 ${error ? 'border-red-400' : 'border-gray-300'}`}
                autoFocus
                disabled={submitting}
              />
              {error && <p className="text-red-500 text-xs mt-1.5">{error}</p>}
            </div>

            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className="w-full py-3 rounded-lg text-white font-medium text-sm transition-colors disabled:opacity-50 cursor-pointer"
              style={{ backgroundColor: '#3366FF' }}
            >
              {submitting ? '...' : (t.submit || 'Subscribe')}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">{t.or_divider || 'or'}</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Google OAuth */}
          <GoogleLoginButton
            role="student"
            label={t.google_button || 'Continue with Google'}
            extraParams={{ redirect_url: redirectUrl }}
            className="py-3 border-2 rounded-lg justify-center gap-3"
          />
        </div>
      </div>
    </div>
  )
}
